"use client"

import * as React from "react"
import { useWatch } from "react-hook-form"

import { ExamCategoryPickerPanel } from "@/components/exam-category-picker-panel"
import { LeoThinkingWidget } from "@/components/leo-thinking-widget"
import { LEO_SURFACE_CARD_CLASS } from "@/components/question-quality/question-leo-jobs"
import { LeoSpottedMark } from "@/components/question-quality/leo-spotted-header"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Kbd } from "@/components/ui/kbd"
import { Tip } from "@/components/ui/tip"
import { useExamCategories } from "@/hooks/use-exam-categories"
import {
  findCategory,
  getCategoryBreadcrumb,
  type ExamCategory,
} from "@/lib/exam-categories"
import { leadInPlainText } from "@/lib/lead-in-html"
import { cn } from "@/lib/utils"

import { useQuestionComposerContext } from "./composer-context"

const MIN_STEM_LENGTH = 24
const LEO_THINKING_MS = 700
const LEO_RERUN_DEBOUNCE_MS = 900

type MatchStrength = "strong" | "possible"

type CategorySuggestion = {
  id: string
  strength: MatchStrength
  evidence: string[]
}

const SUGGESTION_RULES: Array<{
  id: string
  strength: MatchStrength
  terms: RegExp
}> = [
  {
    id: "laboratory-interpretation-acid-base-interpretation",
    strength: "strong",
    terms: /\b(ph|acid|base|charge|anion|cation|bicarbonate)\b/i,
  },
  {
    id: "pharmacology-select-the-appropriate-medication",
    strength: "strong",
    terms: /\b(medication|drug|dose|prescri\w*|administer|therapy)\b/i,
  },
  {
    id: "infectious-disease-identify-the-organism",
    strength: "strong",
    terms: /\b(infection|organism|bacteria|virus|fung\w*|pathogen)\b/i,
  },
  {
    id: "emergency-recognition-identify-unstable-patient",
    strength: "possible",
    terms: /\b(unstable|emergency|rapid response|immediate|first action)\b/i,
  },
  {
    id: "patient-safety-prevent-medical-errors",
    strength: "possible",
    terms: /\b(error|safety|contraindicat\w*|wrong patient|wrong dose)\b/i,
  },
]

const STRENGTH_LABEL: Record<MatchStrength, string> = {
  strong: "Strong match",
  possible: "Possible match",
}

function categoryPath(category: ExamCategory, categories: ExamCategory[]) {
  return getCategoryBreadcrumb(categories, category.id)
    .slice(0, -1)
    .map((item) => item.name)
    .join(" / ")
}

function suggestCategories(
  stem: string,
  categories: ExamCategory[]
): CategorySuggestion[] {
  return SUGGESTION_RULES.flatMap((rule) => {
    if (!findCategory(categories, rule.id)) return []
    const global = new RegExp(rule.terms.source, "gi")
    const terms = [
      ...new Set([...stem.matchAll(global)].map((match) => match[0])),
    ]
    if (terms.length === 0) return []
    return [{ id: rule.id, strength: rule.strength, evidence: terms }]
  }).slice(0, 3)
}

function useCategoryIds() {
  const { form } = useQuestionComposerContext()
  return useWatch({ control: form.control, name: "categoryIds" }) ?? []
}

/** Single header for the Categories drill-in: back, title, count. No collapse. */
export function ComposerCategoryHeader({
  titleClassName,
}: {
  titleClassName?: string
}) {
  const { setInspectorView } = useQuestionComposerContext()
  const count = useCategoryIds().length

  return (
    <>
      <Tip
        side="bottom"
        label={
          <span className="inline-flex items-center gap-2">
            Back to properties <Kbd>Esc</Kbd>
          </span>
        }
      >
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          className="-ms-1 shrink-0"
          aria-label="Back to properties"
          aria-keyshortcuts="Escape"
          onClick={() => setInspectorView("settings")}
        >
          <i className="fa-light fa-arrow-left" aria-hidden="true" />
        </Button>
      </Tip>
      <h2
        className={cn(
          "m-0 min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-foreground",
          titleClassName
        )}
      >
        Categories
      </h2>
      {count > 0 ? (
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {count} selected
        </span>
      ) : null}
    </>
  )
}

type LeoRun =
  | { status: "waiting" }
  | { status: "thinking" }
  | { status: "ready"; suggestions: CategorySuggestion[] }

function LeoCategorySuggestions({
  stem,
  categories,
  selectedIds,
  leoIds,
  disabled,
  onAdd,
}: {
  stem: string
  categories: ExamCategory[]
  selectedIds: string[]
  leoIds: Set<string>
  disabled?: boolean
  onAdd: (ids: string[]) => void
}) {
  const stemReady = stem.trim().length >= MIN_STEM_LENGTH
  const [run, setRun] = React.useState<LeoRun>(
    stemReady ? { status: "thinking" } : { status: "waiting" }
  )
  const [dismissedIds, setDismissedIds] = React.useState<string[]>([])
  const hasRunRef = React.useRef(false)
  const categoriesRef = React.useRef(categories)

  React.useEffect(() => {
    categoriesRef.current = categories
  }, [categories])

  React.useEffect(() => {
    if (!stemReady) {
      setRun({ status: "waiting" })
      return
    }
    setRun({ status: "thinking" })
    const delay = hasRunRef.current ? LEO_RERUN_DEBOUNCE_MS : LEO_THINKING_MS
    const timer = window.setTimeout(() => {
      hasRunRef.current = true
      setDismissedIds([])
      setRun({
        status: "ready",
        suggestions: suggestCategories(stem, categoriesRef.current),
      })
    }, delay)
    return () => window.clearTimeout(timer)
  }, [stem, stemReady])

  const selected = new Set(selectedIds)
  const visible =
    run.status === "ready"
      ? run.suggestions.filter(
          (suggestion) =>
            !dismissedIds.includes(suggestion.id) && !selected.has(suggestion.id)
        )
      : []
  const addedCount =
    run.status === "ready"
      ? run.suggestions.filter(
          (suggestion) =>
            selected.has(suggestion.id) && leoIds.has(suggestion.id)
        ).length
      : 0
  const pendingStrong = visible.filter(
    (suggestion) => suggestion.strength === "strong"
  )

  return (
    <section
      aria-labelledby="leo-categories-heading"
      aria-busy={run.status === "thinking"}
      aria-live="polite"
      className={cn(LEO_SURFACE_CARD_CLASS, "flex flex-col gap-3")}
    >
      <div className="flex items-center gap-2">
        <LeoSpottedMark />
        <h3
          id="leo-categories-heading"
          className="m-0 min-w-0 flex-1 text-sm font-semibold text-foreground"
        >
          Leo suggests
        </h3>
        {run.status === "ready" && visible.length > 0 ? (
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {visible.length}
          </span>
        ) : null}
      </div>

      {run.status === "waiting" ? (
        <p className="m-0 text-[0.8125rem] leading-5 text-foreground">
          Write the question stem and Leo will suggest categories here.
        </p>
      ) : null}

      {run.status === "thinking" ? <LeoThinkingWidget size="sm" /> : null}

      {run.status === "ready" ? (
        <>
          {visible.length === 0 ? (
            <p className="m-0 text-[0.8125rem] leading-5 text-foreground">
              {run.suggestions.length === 0
                ? "No matching categories for this stem. Browse below to add one."
                : addedCount > 0
                  ? `${addedCount} added to your categories. Browse below to add others.`
                  : "All suggestions are handled. Browse below to add others."}
            </p>
          ) : (
            <ul
              className="m-0 flex list-none flex-col gap-2 p-0"
              aria-label="Suggested categories"
            >
              {visible.map((suggestion) => {
                const category = findCategory(categories, suggestion.id)
                if (!category) return null
                const path = categoryPath(category, categories)
                return (
                  <li
                    key={suggestion.id}
                    className="flex min-w-0 flex-col gap-2 rounded-lg border border-border/80 bg-background/85 p-2.5 shadow-xs"
                  >
                    <div className="flex min-w-0 items-start gap-2">
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm leading-5 font-medium text-foreground">
                          {category.name}
                        </span>
                        {path ? (
                          <span className="block text-xs leading-relaxed break-words text-muted-foreground">
                            {path}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-2xs font-medium",
                          suggestion.strength === "strong"
                            ? "bg-brand/10 text-brand"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {STRENGTH_LABEL[suggestion.strength]}
                      </span>
                    </div>
                    <p className="m-0 text-xs leading-relaxed text-muted-foreground">
                      The stem mentions{" "}
                      {suggestion.evidence.map((term, index) => (
                        <React.Fragment key={term}>
                          {index > 0
                            ? index === suggestion.evidence.length - 1
                              ? " and "
                              : ", "
                            : null}
                          <span className="font-medium text-foreground">
                            {term}
                          </span>
                        </React.Fragment>
                      ))}
                      .
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={disabled}
                          onClick={() => onAdd([suggestion.id])}
                        >
                          <i className="fa-light fa-plus" aria-hidden="true" />
                          Add
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={disabled}
                          aria-label={`Dismiss ${category.name}`}
                          onClick={() =>
                            setDismissedIds((current) => [
                              ...current,
                              suggestion.id,
                            ])
                          }
                        >
                          Not relevant
                        </Button>
                      </div>
                  </li>
                )
              })}
            </ul>
          )}

          {pendingStrong.length > 1 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-fit bg-transparent"
              disabled={disabled}
              onClick={() => onAdd(pendingStrong.map((item) => item.id))}
            >
              Add all strong matches ({pendingStrong.length})
            </Button>
          ) : null}

          {visible.length > 0 ? (
            <p className="m-0 text-xs text-muted-foreground">
              {addedCount > 0
                ? `${addedCount} added so far. Nothing else is added until you choose.`
                : "Based on the question stem. Nothing is added until you choose."}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

export function ComposerCategoryDrillIn() {
  const { form, disabled, setInspectorView, watchedLeadIn } =
    useQuestionComposerContext()
  const { categories, tree } = useExamCategories()
  const categoryIds = useCategoryIds()
  const stem = leadInPlainText(watchedLeadIn ?? "")
  const [leoIds, setLeoIds] = React.useState<Set<string>>(() => new Set())
  const [ancestorReview, setAncestorReview] = React.useState<{
    childId: string
    ancestorIds: string[]
    fromLeo: boolean
  } | null>(null)
  const [ancestorChoices, setAncestorChoices] = React.useState<string[]>([])

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      setInspectorView("settings")
    }
    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [setInspectorView])

  const selected = categoryIds
    .map((id) => findCategory(categories, id))
    .filter((category): category is ExamCategory => Boolean(category))
  const selectedGroups = React.useMemo(() => {
    const groups = new Map<
      string,
      { root: ExamCategory; categories: ExamCategory[] }
    >()
    for (const category of selected) {
      const root =
        getCategoryBreadcrumb(categories, category.id)[0] ?? category
      const current = groups.get(root.id)
      if (current) current.categories.push(category)
      else groups.set(root.id, { root, categories: [category] })
    }
    return [...groups.values()]
  }, [categories, selected])
  const reviewAncestors = ancestorReview
    ? ancestorReview.ancestorIds
        .map((id) => findCategory(categories, id))
        .filter((category): category is ExamCategory => Boolean(category))
    : []

  const writeIds = React.useCallback(
    (next: string[]) => {
      form.setValue("categoryIds", next, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [form]
  )

  const offerAncestors = React.useCallback(
    (childId: string, currentIds: string[], fromLeo: boolean) => {
      const missingAncestors = getCategoryBreadcrumb(categories, childId)
        .slice(0, -1)
        .map((category) => category.id)
        .filter((id) => !currentIds.includes(id))
      if (missingAncestors.length === 0) {
        setAncestorReview(null)
        setAncestorChoices([])
        return
      }
      const immediateParentId = missingAncestors.at(-1)!
      setAncestorReview({
        childId,
        ancestorIds: missingAncestors,
        fromLeo,
      })
      setAncestorChoices([immediateParentId])
    },
    [categories]
  )

  const addCategories = React.useCallback(
    (ids: string[], fromLeo: boolean) => {
      const fresh = ids.filter((id) => !categoryIds.includes(id))
      if (fresh.length === 0) return
      const next = [...categoryIds, ...fresh]
      writeIds(next)
      setLeoIds((current) => {
        const updated = new Set(current)
        for (const id of fresh) {
          if (fromLeo) updated.add(id)
          else updated.delete(id)
        }
        return updated
      })
      offerAncestors(fresh[0]!, next, fromLeo)
    },
    [categoryIds, offerAncestors, writeIds]
  )

  const removeCategory = React.useCallback(
    (categoryId: string) => {
      writeIds(categoryIds.filter((id) => id !== categoryId))
      setAncestorReview((current) =>
        current?.childId === categoryId ? null : current
      )
    },
    [categoryIds, writeIds]
  )

  const acceptAncestors = React.useCallback(() => {
    if (!ancestorReview || ancestorChoices.length === 0) return
    const fresh = ancestorChoices.filter((id) => !categoryIds.includes(id))
    const next = [...categoryIds, ...fresh]
    writeIds(next)
    if (ancestorReview.fromLeo) {
      setLeoIds((current) => new Set([...current, ...fresh]))
    }
    setAncestorReview(null)
    setAncestorChoices([])
  }, [ancestorChoices, ancestorReview, categoryIds, writeIds])

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <fieldset disabled={disabled} className="contents">
        <LeoCategorySuggestions
          stem={stem}
          categories={categories}
          selectedIds={categoryIds}
          leoIds={leoIds}
          disabled={disabled}
          onAdd={(ids) => addCategories(ids, true)}
        />

        <section
          className="flex min-w-0 flex-col gap-3"
          aria-labelledby="selected-categories-heading"
        >
          <h3
            id="selected-categories-heading"
            className="m-0 text-sm font-semibold text-foreground"
          >
            Your categories
          </h3>

          {selected.length === 0 ? (
            <p className="m-0 rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
              None yet. Add a suggestion or browse below.
            </p>
          ) : (
            <div
              className="flex min-w-0 flex-col gap-3"
              aria-label="Selected categories"
            >
              {selectedGroups.map((group) => (
                <section
                  key={group.root.id}
                  className="min-w-0"
                  aria-labelledby={`selected-tree-${group.root.id}`}
                >
                  <div className="mb-1 flex items-center gap-2 px-1">
                    <h4
                      id={`selected-tree-${group.root.id}`}
                      className="m-0 min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground"
                    >
                      {group.root.treeName ?? group.root.name}
                    </h4>
                    <span className="text-2xs text-muted-foreground tabular-nums">
                      {group.categories.length}
                    </span>
                  </div>
                  <ul className="m-0 flex min-w-0 list-none flex-col gap-1.5 p-0">
                    {group.categories.map((category) => {
                      const trail = getCategoryBreadcrumb(categories, category.id)
                      const path = trail
                        .slice(1, -1)
                        .map((item) => item.name)
                        .join(" / ")
                      const fromLeo = leoIds.has(category.id)
                      const prompting = ancestorReview?.childId === category.id
                      return (
                        <li
                          key={category.id}
                          className="min-w-0 overflow-hidden rounded-lg border border-border bg-background"
                        >
                          <div className="flex w-full min-w-0 items-center gap-2 py-1.5 ps-3 pe-1.5">
                            <span className="min-w-0 flex-1">
                              <span className="flex min-w-0 items-center gap-1.5">
                                <span className="min-w-0 text-sm leading-5 font-medium text-foreground">
                                  {category.name}
                                </span>
                                {fromLeo ? (
                                  <Tip label="Added from Leo" side="top">
                                    <span
                                      tabIndex={0}
                                      className="inline-flex shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                      <LeoSpottedMark className="size-4 [&>i]:text-[0.6rem]" />
                                      <span className="sr-only">Added from Leo</span>
                                    </span>
                                  </Tip>
                                ) : null}
                                {!category.isActive ? (
                                  <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-2xs font-medium text-muted-foreground">
                                    Archived
                                  </span>
                                ) : null}
                                {group.root.requiresReview ? (
                                  <span className="shrink-0 rounded-full bg-warning/10 px-1.5 py-0.5 text-2xs font-medium text-warning-foreground">
                                    Needs review
                                  </span>
                                ) : null}
                              </span>
                              {path ? (
                                <span className="block text-xs leading-relaxed break-words text-muted-foreground">
                                  {path}
                                </span>
                              ) : null}
                            </span>
                            <Tip label={`Remove ${category.name}`} side="left">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                className="shrink-0"
                                onClick={() => removeCategory(category.id)}
                                aria-label={`Remove ${category.name}`}
                              >
                                <i className="fa-light fa-xmark" aria-hidden="true" />
                              </Button>
                            </Tip>
                          </div>
                          {prompting ? (
                            <div
                              role="group"
                              aria-labelledby={`broader-category-${category.id}`}
                              className="flex flex-col gap-2 border-t border-border bg-muted/40 px-3 py-2.5"
                            >
                              <div>
                                <p
                                  id={`broader-category-${category.id}`}
                                  className="m-0 text-xs font-medium text-foreground"
                                >
                                  Add broader categories?
                                </p>
                                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                                  Each category is added explicitly. Choose only
                                  the levels that apply.
                                </p>
                              </div>
                              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                                {reviewAncestors
                                  .slice()
                                  .reverse()
                                  .map((ancestor) => {
                                    const checked = ancestorChoices.includes(
                                      ancestor.id
                                    )
                                    const ancestorPath = getCategoryBreadcrumb(
                                      categories,
                                      ancestor.id
                                    )
                                      .map((item) => item.name)
                                      .join(" / ")
                                    return (
                                      <li key={ancestor.id}>
                                        <label className="flex min-h-8 cursor-pointer items-start gap-2 rounded-md px-1 py-1 hover:bg-muted">
                                          <Checkbox
                                            checked={checked}
                                            onCheckedChange={() =>
                                              setAncestorChoices((current) =>
                                                checked
                                                  ? current.filter(
                                                      (id) => id !== ancestor.id
                                                    )
                                                  : [...current, ancestor.id]
                                              )
                                            }
                                            aria-label={`Add ${ancestor.name}`}
                                          />
                                          <span className="min-w-0">
                                            <span className="block text-xs font-medium text-foreground">
                                              {ancestor.name}
                                            </span>
                                            <span className="block truncate text-2xs text-muted-foreground">
                                              {ancestorPath}
                                            </span>
                                          </span>
                                        </label>
                                      </li>
                                    )
                                  })}
                              </ul>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={ancestorChoices.length === 0}
                                  onClick={acceptAncestors}
                                >
                                  Add selected ({ancestorChoices.length})
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setAncestorReview(null)
                                    setAncestorChoices([])
                                  }}
                                >
                                  Not now
                                </Button>
                              </div>
                            </div>
                          ) : null}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}

          <h4
            id="manual-categories-heading"
            className="m-0 mt-1 text-xs font-medium text-muted-foreground"
          >
            Browse all categories
          </h4>
          <div
            role="group"
            aria-labelledby="manual-categories-heading"
            className="min-h-[20rem] overflow-hidden rounded-lg border border-border bg-background"
          >
            <ExamCategoryPickerPanel
              categories={categories}
              tree={tree}
              value={categoryIds}
              multiple
              autoFocusSearch={false}
              navigationMode="drill-in"
              onChange={(next) => {
                const added = next.find((id) => !categoryIds.includes(id))
                const removed = categoryIds.find((id) => !next.includes(id))
                if (added) addCategories([added], false)
                else if (removed) removeCategory(removed)
              }}
            />
          </div>
        </section>
      </fieldset>
    </div>
  )
}
